import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import periodoService from '../../services/periodo.service';
import PeriodoCorrecoesModal from './PeriodoCorrecoesModal';
import PeriodoFecharModal from './PeriodoFecharModal';
import SePode from '../../components/SePode';   // r88
import BrowseHeader from '../../components/BrowseHeader';                    // r109
import { iconeDaRotina } from '../../constants/iconesRotinas';               // r109

// ════════════════════════════════════════════════════════════════════════════
// PeriodosPage — Sistema / Períodos de Lançamentos
// r61: itens 1, 2, 3, 4, 6 (visual + filtro de ano)
// ════════════════════════════════════════════════════════════════════════════

const COR = {
  azul:    '#003399', azulBg:  '#eff6ff',
  borda:   '#e8eaed', texto:   '#1a1a2e', muted: '#888',
  branco:  '#fff',    cinzaBg: '#fafafa',  cinzaSt: '#f3f4f6',
  verde:   '#10b981',
  vermelho:'#dc2626', vermBg:  '#fff0f0',
  amarelo: '#d97706', amlBg:   '#fffbeb',
  laranja: '#f97316',
};

const STATUS_PERIODO = {
  A: { label: 'Aberto',     cor: COR.verde   },
  F: { label: 'Fechado',    cor: '#4b5563', solido: true },
  C: { label: 'Correções',  cor: COR.amarelo },
};

const MESES = [
  '', 'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

function Pill({ label, cor, solido }) {
  // r109: alinhado ao padrão TagCliente — fontSize 12, weight 500, borda na cor da fonte
  //       Variante sólida (texto branco em fundo cheio) mantém sem borda visível
  //       porque a cor seria a mesma do fundo.
  if (solido) {
    return (
      <span style={{
        display: 'inline-block', padding: '2px 10px',
        borderRadius: '20px', fontSize: '12px', fontWeight: '500',
        backgroundColor: cor, color: COR.branco,
        whiteSpace: 'nowrap', letterSpacing: '0.3px',
        border: `1px solid ${cor}`,           // r109: borda na própria cor (mantém forma)
      }}>{label}</span>
    );
  }
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px',
      borderRadius: '20px', fontSize: '12px', fontWeight: '500',
      backgroundColor: cor + '22', color: cor,
      whiteSpace: 'nowrap',
      border: `1px solid ${cor}`,           // r109: borda na cor da fonte
    }}>{label}</span>
  );
}

function formatarDtHora(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

// ─── <MultiSelect> — adaptado do LancamentosHorasPage ───────────────────────
function MultiSelect({ label, opcoes, valores, onChange, largura }) {
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState('');
  const containerRef = useRef(null);
  const inputBuscaRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setAberto(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (aberto) {
      setBusca('');
      setTimeout(() => inputBuscaRef.current?.focus(), 50);
    }
  }, [aberto]);

  const toggleValor = (v) => {
    if (valores.includes(v)) onChange(valores.filter(x => x !== v));
    else onChange([...valores, v]);
  };
  const limpar = (e) => { e.stopPropagation(); onChange([]); };

  const opcoesFiltradas = busca.trim()
    ? opcoes.filter(o => String(o.label || '').toLowerCase().includes(busca.trim().toLowerCase()))
    : opcoes;

  let textoBotao;
  if (valores.length === 0) textoBotao = label;
  else if (valores.length === 1) {
    const op = opcoes.find(o => o.v === valores[0]);
    textoBotao = op?.label || String(valores[0]);
  } else textoBotao = `${valores.length} selecionados`;

  const temSelecao = valores.length > 0;

  return (
    <div ref={containerRef} style={{ position: 'relative', width: largura || '160px' }}>
      <button
        type="button"
        onClick={() => setAberto(a => !a)}
        style={{
          width: '100%', height: '28px',
          padding: '0 8px',
          paddingRight: temSelecao ? '40px' : '26px',
          border: `1px solid ${temSelecao ? COR.azul : COR.borda}`,
          borderRadius: '4px',
          backgroundColor: temSelecao ? COR.azulBg : COR.branco,
          color: temSelecao ? COR.azul : COR.texto,
          fontSize: '13px', fontWeight: temSelecao ? '600' : '500',
          cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: '4px',
          fontFamily: "'Manrope', sans-serif",
          textAlign: 'left',
          overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
          position: 'relative',
        }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>{textoBotao}</span>
        <span style={{
          position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)',
          display: 'flex', alignItems: 'center', pointerEvents: 'none',
        }}>
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <path d="M2.5 4l2.5 2.5L7.5 4" stroke="currentColor"
                  strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </span>
        {temSelecao && (
          <span role="button" onClick={limpar}
            style={{
              position: 'absolute', right: '22px', top: '50%', transform: 'translateY(-50%)',
              width: '14px', height: '14px', borderRadius: '50%',
              backgroundColor: COR.azul, color: COR.branco,
              fontSize: '14px', fontWeight: '700', lineHeight: 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer',
            }} title="Limpar"
          >×</span>
        )}
      </button>

      {aberto && (
        <div style={{
          position: 'absolute', top: '32px', left: 0, right: 0,
          maxHeight: '260px', overflow: 'hidden',
          backgroundColor: COR.branco,
          border: `1px solid ${COR.borda}`, borderRadius: '4px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          zIndex: 100,
          display: 'flex', flexDirection: 'column',
        }}>
          <div style={{ padding: '6px 8px', borderBottom: `1px solid ${COR.borda}`, backgroundColor: COR.cinzaBg }}
               onMouseDown={e => e.stopPropagation()}>
            <input
              ref={inputBuscaRef}
              type="text" value={busca}
              onChange={e => setBusca(e.target.value)}
              placeholder="Buscar..."
              style={{
                width: '100%', height: '24px', padding: '0 8px',
                border: `1px solid ${COR.borda}`, borderRadius: '3px',
                fontSize: '13px', fontFamily: "'Manrope', sans-serif",
                outline: 'none',
              }}
            />
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {opcoesFiltradas.length === 0 ? (
              <div style={{ padding: '10px 14px', fontSize: '13px', color: COR.muted, fontStyle: 'italic' }}>
                {busca.trim() ? 'Nada encontrado.' : 'Sem opções.'}
              </div>
            ) : opcoesFiltradas.map(op => {
              const marcado = valores.includes(op.v);
              return (
                <label key={op.v} style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  padding: '7px 12px', cursor: 'pointer',
                  fontSize: '13px', color: COR.texto,
                  backgroundColor: marcado ? COR.azulBg : COR.branco,
                  borderBottom: `1px solid ${COR.borda}`,
                }}
                onMouseDown={e => e.stopPropagation()}>
                  <input type="checkbox" checked={marcado}
                         onChange={() => toggleValor(op.v)}
                         style={{ cursor: 'pointer' }} />
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {op.label}
                  </span>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}


function PeriodosPage() {
  const { usuario } = useAuth();
  const [periodos, setPeriodos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');

  const [modalFechar, setModalFechar] = useState(null);
  const [modalCorrecoes, setModalCorrecoes] = useState(null);

  // r61 item 6: filtro de ano (multiselect), default = ano atual
  const anoAtual = new Date().getFullYear();
  const [filtroAnos, setFiltroAnos] = useState([anoAtual]);

  const carregar = useCallback(async () => {
    try {
      setCarregando(true);
      setErro('');
      const params = {};
      if (filtroAnos.length > 0) params.anos = filtroAnos.join(',');
      const r = await periodoService.listar(params);
      setPeriodos(r);
    } catch (err) {
      setErro('Erro ao carregar períodos.');
    } finally {
      setCarregando(false);
    }
  }, [filtroAnos]);

  useEffect(() => { carregar(); }, [carregar]);

  // Opções de ano (atual + 1 .. atual - 5)
  const opcoesAno = [];
  for (let y = anoAtual + 1; y >= anoAtual - 5; y--) {
    opcoesAno.push({ v: y, label: String(y) });
  }

  const finalizarCorrecoes = async (p) => {
    if (!window.confirm(`Finalizar correções do período ${String(p.mes).padStart(2,'0')}/${p.ano}? O período voltará pra Fechado.`)) return;
    try {
      await periodoService.finalizarCorrecoes(p.id, usuario.id);
      carregar();
    } catch (err) {
      alert(err.response?.data?.mensagem || err.message);
    }
  };

  // r61 itens 1, 2, 3, 4: colunas reorganizadas
  //   1: removida coluna "Período" (mm/aaaa)
  //   2: Status na 1ª coluna
  //   3: headers Title Case + negrito + sem uppercase
  //   4: align dos numéricos = right (header + célula iguais)
  const COLUNAS = [
    { label: 'Status',      align: 'left'  },
    { label: 'Mês/Ano',     align: 'left'  },
    { label: 'Fechado em',  align: 'left'  },
    { label: 'Por',         align: 'left'  },
    { label: 'Lançamentos', align: 'right' },
    { label: 'Pendências',  align: 'right' },
    { label: 'Ações',       align: 'left'  },
  ];

  return (
    <div style={{ fontFamily: "'Manrope', sans-serif" }}>

      {/* r109: BrowseHeader padronizado no topo */}
      <BrowseHeader
        rotina="periodos"
        titulo="Períodos de Lançamentos"
        descricao="Controle mensal de abertura, fechamento e correções dos lançamentos de horas."
      />

      {erro && (
        <div style={{
          padding: '10px 14px', marginBottom: '10px',
          backgroundColor: COR.vermBg, color: COR.vermelho,
          border: `1px solid ${COR.vermelho}`, borderRadius: '6px',
          fontSize: '12px', fontWeight: '600',
        }}>{erro}</div>
      )}

      {/* r61 item 6: barra de filtros */}
      <div style={{
        backgroundColor: COR.branco,
        border: `1px solid ${COR.borda}`, borderRadius: '8px',
        padding: '10px 14px', marginBottom: '10px',
        display: 'flex', alignItems: 'center', gap: '10px',
      }}>
        <span style={{ fontSize: '13px', fontWeight: '600', color: COR.texto }}>
          Filtros
        </span>
        <MultiSelect
          label="Ano"
          opcoes={opcoesAno}
          valores={filtroAnos}
          onChange={setFiltroAnos}
          largura="140px"
        />
      </div>

      <div style={{
        backgroundColor: COR.branco,
        border: `1px solid ${COR.borda}`, borderRadius: '8px',
        overflow: 'hidden', marginBottom: '12px',
      }}>
        <div style={{
          padding: '14px 16px', borderBottom: `1px solid ${COR.borda}`,
          display: 'flex', alignItems: 'center', gap: '10px',
        }}>
          <span style={{ fontSize: '13px', fontWeight: '500', color: COR.muted, flex: 1 }}>
            {periodos.length} período{periodos.length !== 1 ? 's' : ''}
          </span>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ backgroundColor: COR.azul }}>
                {/* r109: 1ª coluna fixa pro ícone SVG da rotina */}
                <th style={{ padding: '10px 14px', width: '36px' }}></th>
                {COLUNAS.map(c => (
                  <th key={c.label} style={{
                    padding: '10px 14px', textAlign: c.align,
                    fontSize: '13px', fontWeight: '700', color: COR.branco,
                    letterSpacing: 'normal',
                    whiteSpace: 'nowrap',
                  }}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {carregando ? (
                <tr><td colSpan={COLUNAS.length + 1} style={{ padding: '30px', textAlign: 'center', color: COR.muted }}>
                  Carregando...
                </td></tr>
              ) : periodos.length === 0 ? (
                <tr><td colSpan={COLUNAS.length + 1} style={{ padding: '30px', textAlign: 'center', color: COR.muted, fontStyle: 'italic' }}>
                  Nenhum período encontrado.
                </td></tr>
              ) : periodos.map((p, i) => {
                const stat = STATUS_PERIODO[p.status] || { label: '?', cor: COR.muted };
                const mesNome = `${MESES[p.mes]} ${p.ano}`;
                return (
                  <tr key={p.id} style={{
                    borderBottom: `1px solid ${COR.borda}`,
                    backgroundColor: i % 2 === 0 ? COR.branco : COR.cinzaBg,
                  }}>
                    {/* r109: ícone SVG #003399 da rotina (mesmo do menu lateral) */}
                    <td style={{ padding: '10px 0 10px 14px', verticalAlign: 'middle' }}>
                      {iconeDaRotina('periodos', { width: 20, height: 20, color: COR.azul })}
                    </td>
                    {/* r61 item 2: Status na 1ª coluna */}
                    <td style={{ padding: '10px 14px' }}>
                      <Pill label={stat.label} cor={stat.cor} solido={stat.solido} />
                    </td>
                    <td style={{ padding: '10px 14px', color: COR.texto }}>{mesNome}</td>
                    <td style={{ padding: '10px 14px', color: COR.muted, fontSize: '12px' }}>
                      {p.status === 'A' ? '—' : formatarDtHora(p.fechado_em)}
                    </td>
                    <td style={{ padding: '10px 14px', color: COR.muted, fontSize: '12px' }}>
                      {p.fechado_por_nome || '—'}
                    </td>
                    <td style={{ padding: '10px 14px', color: COR.texto, textAlign: 'right' }}>
                      {p.qtd_lancamentos || 0}
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                      {p.qtd_correcoes_pendentes > 0 ? (
                        <span style={{
                          fontWeight: '700', color: COR.amarelo,
                          padding: '2px 8px', backgroundColor: COR.amarelo + '22',
                          borderRadius: '10px',
                        }}>{p.qtd_correcoes_pendentes}</span>
                      ) : (p.status === 'C' ? '0' : '—')}
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        {p.status === 'A' && (
                          <SePode rotina="periodos" acao="fechar">
                          <button onClick={() => setModalFechar(p)} title="Fechar período"
                            style={{
                              padding: '4px 10px', fontSize: '11px', fontWeight: '600',
                              backgroundColor: COR.azul, color: COR.branco,
                              border: 'none', borderRadius: '4px', cursor: 'pointer',
                            }}>Fechar</button>
                          </SePode>
                        )}
                        {p.status === 'F' && (
                          <SePode rotina="periodos" acao="abrir-correcoes">
                          <button onClick={() => setModalCorrecoes(p)} title="Abrir correções"
                            style={{
                              padding: '4px 10px', fontSize: '11px', fontWeight: '600',
                              backgroundColor: COR.amarelo, color: COR.branco,
                              border: 'none', borderRadius: '4px', cursor: 'pointer',
                            }}>Correções</button>
                          </SePode>
                        )}
                        {p.status === 'C' && (
                          <>
                            <SePode rotina="periodos" acao="abrir-correcoes">
                            <button onClick={() => setModalCorrecoes(p)} title="Gerenciar correções"
                              style={{
                                padding: '4px 10px', fontSize: '11px', fontWeight: '600',
                                backgroundColor: COR.branco, color: COR.amarelo,
                                border: `1px solid ${COR.amarelo}`, borderRadius: '4px', cursor: 'pointer',
                              }}>Gerenciar</button>
                            </SePode>
                            <SePode rotina="periodos" acao="abrir-correcoes">
                            <button onClick={() => finalizarCorrecoes(p)}
                              disabled={(p.qtd_correcoes_pendentes || 0) > 0}
                              title={(p.qtd_correcoes_pendentes || 0) > 0
                                     ? 'Existem pendências. Aguarde todos virarem Executados.'
                                     : 'Finalizar correções (volta pra Fechado)'}
                              style={{
                                padding: '4px 10px', fontSize: '11px', fontWeight: '600',
                                backgroundColor: COR.verde, color: COR.branco,
                                border: 'none', borderRadius: '4px',
                                cursor: (p.qtd_correcoes_pendentes || 0) > 0 ? 'not-allowed' : 'pointer',
                                opacity: (p.qtd_correcoes_pendentes || 0) > 0 ? 0.4 : 1,
                              }}>Finalizar</button>
                            </SePode>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {modalFechar && (
        <PeriodoFecharModal
          periodo={modalFechar}
          onFechar={(ok) => { setModalFechar(null); if (ok) carregar(); }}
        />
      )}
      {modalCorrecoes && (
        <PeriodoCorrecoesModal
          periodo={modalCorrecoes}
          onFechar={(ok) => { setModalCorrecoes(null); if (ok) carregar(); }}
        />
      )}
    </div>
  );
}

export default PeriodosPage;
